import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import DocumentTitle from 'react-document-title';
import { Link } from 'react-router-dom';
import { Col, Row } from 'reactstrap';
import { isEmpty } from 'lodash';
import ApiLoader from '../loading/ApiLoader';
import api from '../../apiConfig';
import { useAxiosHook } from '../../hooks/ApiHooks';
import Book from '../containers/Book';
import icon_magnifying from '../../images/icons/icon_magnifying.svg';
import HomeProgram from '../containers/HomeProgram';
import routeList from '../../routes/routeList';
import ButtonReadings from '../containers/Buttons';

const Home = ({ pageTitle, history }) => {
  const bgImage = useRef();
  const [latestBooks, getLatestBooks] = useAxiosHook(api.dataController, api.readingsData, 'get', null, {
    params: { page: 1, per_page: 4, orderby: 'date', order: 'desc' }
  });

  const [featuredBooks, getFeaturedBooks] = useAxiosHook(api.dataController, api.readingsData, 'get', null, {
    params: { page: 1, per_page: 4, book_featured: 1 }
  });

  const [featuredProject, getFeaturedProject] = useAxiosHook(api.dataController, api.projectsData, 'get', null, {
    params: { page: 1, per_page: 1, project_featured: 1 }
  });

  const headerParallax = () => {
    const yOffset = window.pageYOffset;
    const offsetSpeed = 2.5;
    if (document.body.clientWidth < 992) {
      bgImage.current.style.transform = `translateY(${-50 + yOffset / offsetSpeed}px)`;
    } else {
      bgImage.current.style.transform = `translateY(${-100 + yOffset / offsetSpeed}px)`;
    }
  };

  const headerParallaxPositionRefresh = isLarge => {
    if (isLarge) {
      bgImage.current.style.transform = `translateY(-100px)`;
    } else {
      bgImage.current.style.transform = `translateY(-50px)`;
    }
  };

  const mql = window.matchMedia('(min-width: 992px)');
  const handleMq = ({ matches }) => {
    headerParallaxPositionRefresh(matches);
  };

  useEffect(() => {
    getLatestBooks();
    getFeaturedBooks();
    getFeaturedProject();
    window.addEventListener('scroll', headerParallax);
    mql.addListener(handleMq);
    handleMq(mql);
    return () => {
      window.removeEventListener('scroll', headerParallax);
      mql.removeListener(handleMq);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const books = (latestBooks.data && latestBooks.data.elements) || [];
  const booksFeatured = (featuredBooks.data && featuredBooks.data.elements) || [];
  const projectFeatured = (featuredProject.data && featuredProject.data.elements[0]) || {};
  const projectFeaturedPic =
    !isEmpty(projectFeatured) && projectFeatured.pic !== 'false' ? projectFeatured.pic : '/placeholders/project.png';

  const [searchField, setSearchField] = useState('');
  const handleInputChange = e => {
    setSearchField(e.target.value);
  };

  const searchSubmit = e => {
    e.preventDefault();
    if (searchField !== '') {
      history.push(`${routeList.search}/${searchField}`);
    }
  };

  return (
    <div className="full-wrapper flex-grow-1">
      <DocumentTitle title={pageTitle} />
      <div className="content-wrapper">
        <section className="home-header">
          <div className="home-header-background" ref={bgImage} />
          <div className="home-header-text">
            <h1>Lektury</h1>
            <h3 className="mt-2 mb-5">które możesz mieć zawsze przy sobie</h3>
          </div>
          <div className="home-search">
            <form onSubmit={searchSubmit}>
              <label htmlFor="header-search" className="hidden-label">
                "Wpisz tytuł lub autora książki"
              </label>
              <input
                type="text"
                value={searchField}
                id="header-search"
                onChange={handleInputChange}
                placeholder="Wpisz tytuł lub autora książki"
              />
              <button className="btn-primary" type="submit">
                <img src={icon_magnifying} width="18" height="18" alt="Ikona lupy" />
                <span className="i-text ml-2">Szukaj</span>
              </button>
            </form>
          </div>
        </section>
        <section className="text-center home-content-section mb-80">
          <h1 className="main-header">
            <span>Program lektur</span>
          </h1>
          <HomeProgram />
        </section>
        <section className="home-content-section mb-80">
          <h1 className="main-header">
            <span>Najnowsze lektury</span>
          </h1>

          <ApiLoader status={latestBooks.apiStatus}>
            <Row>
              {!!books.length &&
                books.map((book, idx) => (
                  <Col xs="12" sm="6" lg="3" key={idx} className="mb-30">
                    <Book title={book.title} author={book.author_book_link} cover={book.book_cover} slug={book.slug} />
                  </Col>
                ))}
            </Row>
          </ApiLoader>
        </section>
        <section className="home-content-section mb-80">
          <h1 className="main-header">
            <span>Najpopularniejsze lektury</span>
          </h1>
          <ApiLoader status={featuredBooks.apiStatus}>
            <Row className="mb-5">
              {!!booksFeatured.length &&
                booksFeatured.map((book, idx) => (
                  <Col xs="12" sm="6" lg="3" key={idx} className="mb-30">
                    <Book title={book.title} author={book.author_book_link} cover={book.book_cover} slug={book.slug} />
                  </Col>
                ))}
            </Row>
          </ApiLoader>
        </section>
      </div>
      <section id="add-project-banner" className="full-width-banner">
        <div className="full-width-banner-body">
          <h2>Zgłoś swój projekt!</h2>
          <p>Przygotowałeś w swojej szkole ciekawy projekt dydaktyczny?</p>
          <h4>Podziel się nim z innymi!</h4>
          <Link to={routeList.addProject}>
            <button className="btn-primary">Zgłoś projekt</button>
          </Link>
        </div>
      </section>
      <div className="content-wrapper">
        <section className="about">
          <Row className="justify-content-center ml-auto mr-auto mb-100">
            <Col xs="12" md="6" className="d-flex flex-column order-1 order-md-0">
              <h5 className="project-title mb-50">Wybrany projekt</h5>
              <p>
                Serwis Lektury.gov.pl umożliwia dzielenie się ciekawymi projektami. Poniżej znajduje się wyróżniona
                praca.
              </p>
              <p
                className="subtitle-1 project-title mb-3"
                dangerouslySetInnerHTML={{ __html: projectFeatured.title }}
              />
              <p>
                <strong>Autor</strong> <br /> {projectFeatured.author}
              </p>
              <p>
                <strong>O projekcie</strong> <br />
                {projectFeatured.excerpt}
              </p>
              <Link to={`${routeList.project}/${projectFeatured.slug}`} className="mt-auto text-left">
                <ButtonReadings text="Zobacz projekt" color="primary" className="mt-3" />
              </Link>
            </Col>
            <Col sm="12" md="6" lg="6" className="order-0 order-md-1">
              <div className="project-thumbnail-home" style={{ backgroundImage: `url("${projectFeaturedPic}")` }} />
            </Col>
          </Row>
          <Row className="justify-content-center ml-auto mr-auto mb-100">
            <Col sm="12" md="6" lg="6">
              <div className="mb-5">
                <div className="about-books-image"></div>
              </div>
            </Col>
            <Col sm="12" md="6" lg="6">
              <section>
                <h1 className="main-header">
                  <span>O nas</span>
                </h1>
                <div className="about-txt">
                  <p>
                    Rozwój Internetu i postępująca cyfryzacja oznaczać powinny łatwiejszy dostęp do klasycznych dzieł
                    polskiej i światowej literatury.
                    <br />
                    Uruchamiając portal <span className="about-txt-bold">Lektury.gov.pl</span>, oddajemy w Państwa ręce
                    nowe narzędzie ułatwiające zapoznawanie się z utworami literackimi będącymi na liście lektur
                    szkolnych.
                  </p>
                  <p>
                    Serwis <span className="about-txt-bold">Lektury.gov.pl</span> to realizacja postulatu budowy
                    otwartych, dostępnych i przyjaznych cyfrowych zasobów edukacyjnych i kulturowych. Projekt został
                    zainicjowany przez dwa ministerstwa: Ministerstwo Inwestycji i Rozwoju, oraz Ministerstwo
                    Cyfryzacji. Dzięki tej inicjatywie lektury można pobierać przez Internet i słuchać ich wersji audio
                    lub czytać w dowolnym momencie za pomocą wygodnej aplikacji mobilnej.
                  </p>
                  <p>
                    Serwis powstał przede wszystkim z myślą o uczniach i studentach, nauczycielach i wykładowcach, ale
                    zachęcamy do korzystania z niego wszystkich, którzy chcą poznać lub przypomnieć sobie najważniejsze
                    dzieła literatury polskiej i światowej.
                  </p>
                </div>
              </section>
            </Col>
          </Row>
        </section>
      </div>
    </div>
  );
};

Home.defaultProps = {
  pageTitle: 'Lektury'
};
Home.propTypes = {
  pageTitle: PropTypes.string
};

export default Home;
